import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Monitor, Tablet, Smartphone, Sparkles, ArrowRight } from "lucide-react";
import { fontStyle, loadGameFonts } from "@/lib/fonts";
import { overlayGradient, BUTTON_RADIUS } from "@/lib/gameStyle";
import { resolveImageUrl } from "@/lib/images";

const DEVICES = [
  {
    id: "desktop",
    Icon: Monitor,
    frame: "aspect-video w-full max-w-[440px]",
    titleSize: "text-lg",
    pick: (g) => ({ bg: g.background_image, own: true }),
  },
  {
    id: "tablet",
    Icon: Tablet,
    frame: "aspect-[3/4] w-full max-w-[270px]",
    titleSize: "text-base",
    pick: (g) => ({
      bg: g.background_image_tablet || g.background_image,
      own: Boolean(g.background_image_tablet),
    }),
  },
  {
    id: "mobile",
    Icon: Smartphone,
    frame: "aspect-[9/16] w-full max-w-[200px]",
    titleSize: "text-sm",
    pick: (g) => ({
      bg: g.background_image_mobile || g.background_image,
      own: Boolean(g.background_image_mobile),
    }),
  },
];

/**
 * DevicePreview — miniature "player view" of the public game page rendered
 * inside the editors. Tabs PC / Tablet / Mobile show the background picked
 * for each device (with PC fallback) plus title, description, colors and
 * custom fonts, so merchants can judge legibility without leaving the editor.
 */
export default function DevicePreview({ game }) {
  const { t } = useTranslation();
  const [device, setDevice] = useState("mobile");

  useEffect(() => {
    if (game?.fonts) loadGameFonts(game.fonts);
  }, [game?.fonts]);

  if (!game) return null;
  const d = DEVICES.find((x) => x.id === device);
  const { bg, own } = d.pick(game);
  const bgUrl = resolveImageUrl(bg);
  const showFallbackHint = device !== "desktop" && bg && !own;

  return (
    <div data-testid="device-preview">
      <div className="flex items-center gap-1.5 mb-3">
        {DEVICES.map(({ id, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setDevice(id)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-colors ${
              device === id
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`device-preview-tab-${id}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {t(`bg.device_${id}`, id === "desktop" ? "PC" : id === "tablet" ? "Tablette" : "Mobile")}
          </button>
        ))}
      </div>

      <div
        className={`relative ${d.frame} mx-auto rounded-2xl border border-border overflow-hidden shadow-lg bg-background`}
        data-testid="device-preview-frame"
      >
        {bgUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${bgUrl})` }}
              data-testid="device-preview-bg"
            />
            <div
              className="absolute inset-0"
              style={{ background: overlayGradient(game) }}
            />
          </>
        ) : (
          <div
            className="absolute inset-0 qrise-ambient-aqua"
            data-testid="device-preview-bg-ambient"
          />
        )}

        <div className="relative h-full flex flex-col items-center px-3 pt-[9%] pb-[6%] text-center">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/25 bg-black/30 backdrop-blur text-[7px] font-medium text-white/90">
            <Sparkles className="h-2 w-2" />
            {t("play.page_badge", "Jeu QRISE")}
          </div>
          <div
            className={`mt-2 ${d.titleSize} font-semibold tracking-tight leading-tight line-clamp-2`}
            style={{
              color: game.title_color || (bgUrl ? "#ffffff" : undefined),
              ...fontStyle(game.fonts, "title"),
            }}
            data-testid="device-preview-title"
          >
            {game.title || t("game_editor.preview_title")}
          </div>
          {game.description ? (
            <div
              className="mt-1 text-[8px] leading-snug line-clamp-2 opacity-90"
              style={{
                color:
                  game.description_color || (bgUrl ? "#e6e8f0" : undefined),
                ...fontStyle(game.fonts, "description"),
              }}
              data-testid="device-preview-description"
            >
              {game.description}
            </div>
          ) : null}

          <div className="mt-auto w-[88%] rounded-xl bg-white/95 dark:bg-zinc-900/95 shadow-md p-2.5 text-left">
            <div className="text-[7px] font-semibold text-zinc-800 dark:text-zinc-100 mb-1.5">
              {t("play.your_info", "Entrez vos infos pour jouer")}
            </div>
            <div className="space-y-1">
              <div className="h-2.5 rounded bg-zinc-200 dark:bg-zinc-700" />
              <div className="h-2.5 rounded bg-zinc-200 dark:bg-zinc-700" />
            </div>
            <div
              className="mt-2 h-5 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 flex items-center justify-center gap-1 text-[7px] font-medium"
              style={{
                ...fontStyle(game.fonts, "buttons"),
                backgroundColor: game.button_bg_color || undefined,
                color: game.button_text_color || undefined,
                borderRadius: BUTTON_RADIUS[game.button_radius] || undefined,
              }}
              data-testid="device-preview-button"
            >
              {game.button_continue_label || t("play.continue", "Continuer")}
              <ArrowRight className="h-2 w-2" />
            </div>
          </div>
        </div>
      </div>

      {showFallbackHint && (
        <p
          className="mt-2 text-[10px] text-muted-foreground text-center"
          data-testid="device-preview-fallback-hint"
        >
          {t(
            "bg.preview_fallback",
            "Image PC utilisée (aucun fond défini pour cet appareil)."
          )}
        </p>
      )}
    </div>
  );
}
